import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";

import { PaymentSheet } from "@/components/PaymentSheet";
import {
  ASQAmount,
  EmptyState,
  ErrorState,
  Panel,
  RegistryBlock,
  SheetLabel,
  Skeleton,
  Status,
} from "@/components/primitives";
import { api } from "@/lib/api";
import { formatASQ, formatDate, daysUntil } from "@/lib/format";
import { usePayment } from "@/lib/use-payment";

/**
 * Membership — plan §12, §37A.
 *
 * The metaphor is a digital access pass, and the composition follows §62: the
 * proposition on the left seven columns, a sticky payment console on the right
 * four. The giant `3 ASQ` is the hero datum rather than a row in a pricing
 * table, which is what §37A asks for and what keeps this from looking like
 * every SaaS plan page.
 */
export function MembershipPage() {
  const { address, isConnected } = useAccount();
  const queryClient = useQueryClient();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { state, pay, reset } = usePayment();

  const { data: plan, isPending, isError, error } = useQuery({
    queryKey: ["membership"],
    queryFn: api.membership,
  });

  const { data: status } = useQuery({
    queryKey: ["membership-status", address],
    queryFn: () => api.membershipStatus(address!),
    enabled: Boolean(address),
  });

  if (isPending) {
    return (
      <div className="sheet space-y-6 py-16">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !plan) {
    return (
      <div className="sheet py-16">
        <ErrorState title="Tidak dapat memuat membership" code="LOAD_FAILED">
          {error instanceof Error ? error.message : "Coba muat ulang halaman."}
        </ErrorState>
      </div>
    );
  }

  const utility = plan.utility;
  const active = status?.active === true;

  const closeSheet = () => {
    setSheetOpen(false);
    if (state.step === "done") {
      // The grant only exists after verification, so the cached status is stale
      // exactly once — here. Refetching on close is what makes the page agree
      // with the success screen the user just read.
      queryClient.invalidateQueries({ queryKey: ["membership-status", address] });
      queryClient.invalidateQueries({ queryKey: ["utilities"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard", address] });
    }
    reset();
  };

  return (
    <div className="sheet grid gap-12 py-16 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <SheetLabel coordinate="ASETQU_MEMBERSHIP">Access pass</SheetLabel>
        <h1 className="display-section mt-5 text-paper-50">{plan.name}</h1>

        <p className="mt-6 max-w-lg text-base text-paper-300/70">
          Satu pembayaran ASQ membuka akses member selama {plan.durationDays} hari.
          Pembayarannya bersifat final — masuk langsung ke Membership Permanent
          Vault dan tidak dapat ditarik kembali.
        </p>

        {/* The hero datum. */}
        <div className="mt-12 border-y border-ink-700 py-10">
          <SheetLabel>Harga tetap</SheetLabel>
          <p className="mt-4">
            <ASQAmount raw={plan.priceRaw} size="xl" />
          </p>
          <p className="micro mt-4">
            {plan.durationDays} hari · harga terkunci di kontrak, bukan di server
          </p>
        </div>

        <div className="mt-10">
          <SheetLabel>Yang didapat</SheetLabel>
          <ul className="mt-5 divide-y divide-ink-700 border-t border-ink-700">
            {plan.benefits.map((benefit) => (
              <li key={benefit} className="flex items-baseline gap-4 py-3.5">
                <span aria-hidden="true" className="h-px w-6 shrink-0 bg-asq-emerald" />
                <span className="text-sm text-paper-100">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* The payment console. Sticky on desktop, per §62. */}
      <aside className="lg:col-span-5">
        <div className="lg:sticky lg:top-24">
          {active && status ? (
            <Panel marked className="p-6">
              <Status state="ok">Membership aktif</Status>
              <p className="mt-6">
                <SheetLabel>Berlaku hingga</SheetLabel>
              </p>
              <p className="datum mt-2 text-2xl text-paper-50">
                {formatDate(status.expiresAt)}
              </p>
              <p className="micro mt-2">{daysUntil(status.expiresAt)} hari tersisa</p>

              <p className="mt-6 text-xs text-paper-300/60">
                Perpanjangan menambah {plan.durationDays} hari di atas sisa masa
                aktif — tidak menghapus hari yang sudah dibayar.
              </p>

              <button
                type="button"
                className="btn btn--ghost mt-6 w-full"
                onClick={() => setSheetOpen(true)}
                disabled={!utility?.usable}
              >
                Perpanjang
              </button>
            </Panel>
          ) : (
            <Panel marked className="p-6">
              <SheetLabel>Aktivasi</SheetLabel>

              {!utility ? (
                <div className="mt-4">
                  <ErrorState
                    title="Membership belum tersedia"
                    code={plan.unavailable?.code}
                  >
                    {plan.unavailable?.detail ??
                      "Registry belum menyatakan modul membership aktif, jadi tidak ada pembayaran yang dapat dimulai."}
                  </ErrorState>
                </div>
              ) : (
                <>
                  <p className="mt-4 text-sm text-paper-300/70">
                    Bayar {formatASQ(plan.priceRaw)} ASQ untuk mengaktifkan akses{" "}
                    {plan.durationDays} hari.
                  </p>

                  <div className="mt-6 border-t border-ink-700 pt-5">
                    <RegistryBlock utility={utility} />
                  </div>

                  {!isConnected ? (
                    <p className="micro mt-6 text-warn">
                      Hubungkan wallet untuk melanjutkan
                    </p>
                  ) : null}

                  <button
                    type="button"
                    className="btn btn--primary mt-6 w-full"
                    onClick={() => setSheetOpen(true)}
                    disabled={!isConnected || !utility.usable}
                  >
                    Aktifkan Membership
                  </button>
                </>
              )}
            </Panel>
          )}
        </div>
      </aside>

      {utility ? (
        <PaymentSheet
          open={sheetOpen}
          onClose={closeSheet}
          title="Aktifkan Membership"
          subject={plan.name}
          details={[
            { label: "Durasi", value: `${plan.durationDays} hari` },
            { label: "Utility", value: "ASETQU_MEMBERSHIP" },
          ]}
          amountRaw={plan.priceRaw}
          utility={utility}
          state={state}
          onConfirm={() => pay({ utility: "ASETQU_MEMBERSHIP" })}
        />
      ) : null}
    </div>
  );
}

/** Rendered when a wallet is required but absent. Kept here so the copy for
 *  "connect first" lives next to the flow it blocks. */
export function ConnectFirst() {
  return (
    <EmptyState title="Wallet belum terhubung">
      Hubungkan wallet untuk melihat status dan riwayat pembayaran Anda.
    </EmptyState>
  );
}
